import React, { useEffect, useState } from "react";
import { Loader2, Save, Languages, RotateCcw, MapPin } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const LANGS = [["en", "English"], ["es", "Español"], ["fr", "Français"], ["pt", "Português"], ["de", "Deutsch"]];

// The paragraph that makes each city page read like a local wrote it. Editable per city per
// language, because a template across fifteen pages is exactly what a search engine drops.
export const AdminCityCopy = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [key, setKey] = useState("");
  const [lang, setLang] = useState("en");
  const [draft, setDraft] = useState({ text: "", fallback: "" });
  const [busy, setBusy] = useState("");
  const [signups, setSignups] = useState({});

  useEffect(() => {
    api.get("/admin/city-signups")
      .then((r) => setSignups(Object.fromEntries(r.data.map((x) => [x.key, x.signups]))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/admin/city-copy").then((r) => {
      setRows(r.data);
      if (r.data.length && !key) setKey(r.data[0].key);
    }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!key) return;
    api.get(`/admin/city-copy/${key}`, { params: { lang } })
      .then((r) => setDraft({ text: r.data.text || "", fallback: r.data.fallback || "" }))
      .catch(() => setDraft({ text: "", fallback: "" }));
  }, [key, lang]);

  const row = rows.find((r) => r.key === key);

  const save = async (text) => {
    setBusy("save");
    try {
      await api.post(`/admin/city-copy/${key}`, { text }, { params: { lang } });
      setDraft((d) => ({ ...d, text }));
      const list = await api.get("/admin/city-copy");
      setRows(list.data);
      toast({ title: "Saved", description: `${row?.city} · ${lang.toUpperCase()}` });
    } catch (err) {
      toast({ title: "Could not save", description: err.response?.data?.detail || "Try again." });
    } finally {
      setBusy("");
    }
  };

  const translate = async () => {
    setBusy("translate");
    try {
      const { data } = await api.post(`/admin/city-copy/${key}/translate`, null, { params: { lang } });
      setDraft((d) => ({ ...d, text: data.text }));
      toast({ title: "Draft ready", description: "Read it over, then save." });
    } catch (err) {
      toast({ title: "Could not translate", description: err.response?.data?.detail || "Try again." });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="admin-city-copy">
      <div>
        <h3 className="font-bold text-foreground flex items-center gap-2"><MapPin size={18} className="text-[#16a34a]" /> City page copy</h3>
        <p className="text-sm text-muted-foreground mt-1">
          One paragraph per city, in your words. Blank puts the shipped wording back.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <select value={key} onChange={(e) => setKey(e.target.value)} data-testid="city-copy-city"
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground">
          {rows.map((r) => (
            <option key={r.key} value={r.key}>
              {`${r.city} · ${r.country}${r.edited ? " ✎" : ""} (${r.dealCount})`}
            </option>
          ))}
        </select>
        <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="city-copy-lang"
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground">
          {LANGS.map(([c, label]) => (<option key={c} value={c}>{label}</option>))}
        </select>
        {lang !== "en" && (
          <button onClick={translate} disabled={!!busy} data-testid="city-copy-translate"
            className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm font-bold text-foreground hover:border-[#16a34a] hover:text-[#16a34a] transition-colors disabled:opacity-60">
            {busy === "translate" ? <Loader2 size={15} className="animate-spin" /> : <Languages size={15} />} Draft translation
          </button>
        )}
      </div>

      <textarea value={draft.text} onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
        rows={5} maxLength={1200} data-testid="city-copy-text"
        placeholder={draft.fallback}
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" />
      {!draft.text && draft.fallback && (
        <p className="text-xs text-muted-foreground" data-testid="city-copy-fallback">
          Showing the shipped wording: “{draft.fallback}”
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => save(draft.text)} disabled={!!busy} data-testid="city-copy-save"
          className="inline-flex items-center gap-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-bold px-4 py-2 transition-colors disabled:opacity-60">
          {busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
        </button>
        <button onClick={() => save("")} disabled={!!busy} data-testid="city-copy-reset"
          className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm font-bold text-muted-foreground hover:text-rose-500 hover:border-rose-400 transition-colors disabled:opacity-60">
          <RotateCcw size={15} /> Reset
        </button>
        {row && (
          <a href={`/${row.countrySlug}/${row.citySlug}`} target="_blank" rel="noopener noreferrer"
            data-testid="city-copy-view" className="text-sm font-bold text-[#16a34a] hover:underline">
            View page
          </a>
        )}
        {row && (
          <a href={`/pitch/${row.countrySlug}/${row.citySlug}?lang=${lang}`} target="_blank" rel="noopener noreferrer"
            data-testid="city-pitch-link" className="text-sm font-bold text-[#16a34a] hover:underline">
            Printable sheets
          </a>
        )}
        {row && (
          <span className="text-xs text-muted-foreground" data-testid="city-signups">
            {(signups[row.key] || 0)} shops joined from this city's link
          </span>
        )}
      </div>
    </div>
  );
};

export default AdminCityCopy;
